/**
 * RoleGuard -- protects routes that require a minimum organizational role.
 *
 * Checks the current user's role from useAuthStore against the required
 * minimum role. Role hierarchy: admin > maintainer > member.
 *
 * Behavior:
 * - If the user's role is sufficient, renders children (or <Outlet />).
 * - If insufficient AND `silent` is true, redirects to `fallbackPath` (default /chat).
 * - If insufficient AND `silent` is false, renders a 403-style access denied page.
 */

import { Navigate, Outlet, useLocation } from 'react-router';
import { useIntl, FormattedMessage } from 'react-intl';
import { ShieldWarning } from '@phosphor-icons/react';
import { useAuthStore } from '@/stores/useAuthStore';
import type { ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type OrgRole = 'admin' | 'maintainer' | 'member';

interface RoleGuardProps {
  /** Minimum role required to access this route */
  minimumRole: OrgRole;
  /** When true, silently redirects instead of showing 403. Default: false */
  silent?: boolean;
  /** Redirect path when access is denied. Default: '/' */
  fallbackPath?: string;
  /** Children to render when authorized. Falls back to <Outlet /> */
  children?: ReactNode;
}

// ---------------------------------------------------------------------------
// Role hierarchy (higher index = higher privileges)
// ---------------------------------------------------------------------------

const ROLE_HIERARCHY: Record<OrgRole, number> = {
  member: 0,
  maintainer: 1,
  admin: 2,
};

/**
 * Check if `userRole` meets the minimum required role.
 */
export function hasMinimumRole(userRole: OrgRole, minimumRole: OrgRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[minimumRole];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RoleGuard({
  minimumRole,
  silent = false,
  fallbackPath = '/',
  children,
}: RoleGuardProps) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const intl = useIntl();

  // No user loaded yet (should not happen if wrapped in ProtectedRoute)
  if (!user) {
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  const authorized = hasMinimumRole(user.role, minimumRole);

  if (authorized) {
    return children ? <>{children}</> : <Outlet />;
  }

  // Silent mode: redirect without showing 403
  if (silent) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Explicit 403 access denied page
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4">
      <ShieldWarning
        size={64}
        weight="duotone"
        className="text-error-400"
        aria-hidden="true"
      />
      <h1 className="text-xl font-semibold text-text-primary">
        <FormattedMessage id="admin.guard.accessDenied" />
      </h1>
      <p className="max-w-md text-center text-sm text-text-secondary">
        <FormattedMessage id="admin.guard.accessDeniedDescription" />
      </p>
      <a
        href="/"
        className="mt-2 rounded-lg bg-accent-500 px-4 py-2 text-sm font-medium text-text-on-accent transition-colors hover:bg-accent-600"
        aria-label={intl.formatMessage({ id: 'nav.home' })}
      >
        <FormattedMessage id="admin.guard.goHome" />
      </a>
    </div>
  );
}
